import { useCallback, useMemo } from "react";
import { toast, Slide } from "react-toastify";

// Drop-in replacement for react-simple-snackbar's `useSnackbar()` hook,
// backed by react-toastify instead. Every screen in the app already calls
// this as `const [openSnackbar] = useSnackbar();` then `openSnackbar(msg)`
// (occasionally `useSnackbar({ position, style })` or
// `openSnackbar(msg, "error")` / `openSnackbar(msg, 6000)`) - keeping that
// exact [openSnackbar, closeSnackbar] shape here means switching toast
// libraries only meant swapping the import source across the app, not
// rewriting 70+ call sites.
const TYPES = ["success", "error", "warning", "info", "default"];

// Almost none of those 70+ call sites ever passed an explicit type - they
// just call openSnackbar("Unit deleted successfully") or
// openSnackbar(err?.response?.data?.message). Without inferring a type from
// the message text, every toast in the app would render in the same neutral
// color regardless of whether it's reporting success or failure. Checked in
// this priority order (error first) so a message that happens to contain
// both an error-ish and success-ish word still lands as the more important
// one to get right.
const ERROR_PATTERN =
  /\b(fail(?:ed|ure)?|error|invalid|incorrect|denied|not able|cannot|can'?t|unable|already exists|no longer|expired|not found|wrong|unauthorized|something went wrong)\b/i;
const WARNING_PATTERN =
  /\b(warning|please select|please enter|please choose|please provide|must be|required|low stock|out of stock|no items|no results|minimum|not enough)\b/i;
const SUCCESS_PATTERN =
  /\b(success(?:fully)?|created|updated|added|saved|deleted|removed|completed|applied|activated|deactivated|calculated|logged in|welcome)\b/i;

const inferType = (message) => {
  const text = String(message ?? "");
  if (ERROR_PATTERN.test(text)) return "error";
  if (WARNING_PATTERN.test(text)) return "warning";
  if (SUCCESS_PATTERN.test(text)) return "success";
  return "info";
};

export const useSnackbar = (options = {}) => {
  const position = options.position || "bottom-right";

  const openSnackbar = useCallback(
    (message, second) => {
      let type = null;
      let autoClose = options.autoCloseDuration ?? 3000;

      if (typeof second === "number") {
        autoClose = second;
      } else if (typeof second === "string" && TYPES.includes(second)) {
        type = second;
      }

      return toast(message, {
        type: type || inferType(message),
        autoClose,
        position,
        transition: Slide,
      });
    },
    [options.autoCloseDuration, position]
  );

  const closeSnackbar = useCallback((id) => {
    if (id != null) toast.dismiss(id);
    else toast.dismiss();
  }, []);

  return useMemo(() => [openSnackbar, closeSnackbar], [openSnackbar, closeSnackbar]);
};

export default useSnackbar;
